import { ActionReducerMapBuilder, createAsyncThunk, GetThunkAPI } from '@reduxjs/toolkit';

import { signOutThunk } from '../../../../accounts/slices/auth/thunks/sign-out.thunk.ts';
import { EnvironmentEnum } from '../../../../main/constants/environment.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getAwsConfigUtil } from '../../../../main/utils/aws/aws-config.util.ts';
import { getReadingCrittersClientUtil } from '../../../../main/utils/clients/readingcritters.client.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getUrlUtil } from '../../../../main/utils/url.util.ts';

import { ENVIRONMENT_QUERY_PARAM } from '../../../constants/environment-query-param.ts';
import { mapEnvironmentUrl } from '../../../mappers/environment-url.mapper.ts';
import { checkCustomEnvironmentRequest } from '../../../requests/check-custom-environment.request.ts';
import { getEnvironmentDataRequest } from '../../../requests/get-environment-data.request.ts';

import { setSelectedEnvironment } from '../environment.slice.ts';
import { ENVIRONMENT_SLICE_NAME, EnvironmentSliceState } from '../environment.slice.types.ts';

interface Props {
  environmentName?: string;
}

export const getAndAssignEnvironmentOnInitThunk = createAsyncThunk(
  `${ENVIRONMENT_SLICE_NAME}/getAndAssignEnvironmentOnInit`,
  async ({ environmentName }: Props, thunkAPI: GetThunkAPI<ThunkApiConfigType>) => {
    const isProduction = environmentName === EnvironmentEnum.PRODUCTION;

    try {
      const environmentUrl = mapEnvironmentUrl({ isProduction, environmentName });
      const environmentData = await getEnvironmentDataRequest({ url: environmentUrl });

      if (!environmentData) {
        if (!isProduction) {
          getToasterUtil().error(
            getLocaleUtil().select('environment:integration_data_not_found_switch_production', { environmentName })
          );
          await thunkAPI
            .dispatch(getAndAssignEnvironmentOnInitThunk({ environmentName: EnvironmentEnum.PRODUCTION }))
            .unwrap();
        }
        return;
      }

      const isEnvironmentAvailable = await checkCustomEnvironmentRequest(
        environmentData.apiUrl,
        environmentData.apiKey
      );

      if (!isEnvironmentAvailable) {
        if (!isProduction) {
          getToasterUtil().error(
            getLocaleUtil().select('environment:integration_non_exist_switch_production', { environmentName })
          );
          await thunkAPI
            .dispatch(getAndAssignEnvironmentOnInitThunk({ environmentName: EnvironmentEnum.PRODUCTION }))
            .unwrap();
        }
        return;
      }

      if (!isProduction) {
        getToasterUtil().success(getLocaleUtil().select('environment:integration_selected', { environmentName }));
      }

      const queryResult = getUrlUtil().removeQueryParams(window.location.href, [ENVIRONMENT_QUERY_PARAM]);
      if (queryResult.isRemoved) {
        getUrlUtil().replaceUrlState(queryResult.url);
      }

      // TODO handle reset assets, user data (progress) and sign out
      await thunkAPI.dispatch(signOutThunk()).unwrap();
      getReadingCrittersClientUtil().initialize(environmentData);
      getAwsConfigUtil().configureAws(environmentData);

      thunkAPI.dispatch(setSelectedEnvironment({ ...environmentData, name: environmentName ?? '' }));

      // Make sure the env selection is reflected, then reload the page
      await new Promise((resolve) => setTimeout(resolve, 500));
      window.location.reload();
    } catch (_error) {
      if (!isProduction) {
        getToasterUtil().error(
          getLocaleUtil().select('environment:integration_error_switch_production', { environmentName })
        );
        await thunkAPI.dispatch(getAndAssignEnvironmentOnInitThunk({ environmentName: EnvironmentEnum.PRODUCTION }));
      }
    }
  }
);

export const getAndAssignEnvironmentOnInitThunkExtraReducers = (
  builder: ActionReducerMapBuilder<EnvironmentSliceState>
): void => {
  builder.addCase(getAndAssignEnvironmentOnInitThunk.pending, (state: EnvironmentSliceState) => {
    state.isLoading = true;
  });

  builder.addCase(getAndAssignEnvironmentOnInitThunk.fulfilled, (state: EnvironmentSliceState) => {
    state.isLoading = false;
  });

  builder.addCase(getAndAssignEnvironmentOnInitThunk.rejected, (state: EnvironmentSliceState) => {
    state.isLoading = false;
  });
};
